import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Transporter, TransporterAPI, TransporterVehicleRequest, TransporterDriverRequest } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { StatusBadge } from '../../components/ui/status-badge';
import {
  ArrowLeft,
  Truck,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  Trash2,
  Plus,
  UserCircle,
  FileCheck,
  Loader2,
  Calendar
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import { toast } from '../../components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { format } from 'date-fns';
import TransporterDashboard from '../../components/transporters/TransporterDashboard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export default function TransporterProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transporter, setTransporter] = useState<Transporter | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Vehicle deletion state
  const [isDeletingVehicle, setIsDeletingVehicle] = useState<string | null>(null);

  // Driver deletion state
  const [isDeletingDriver, setIsDeletingDriver] = useState<string | null>(null);

  // Document deletion state
  const [isDeletingDocument, setIsDeletingDocument] = useState<string | null>(null);

  // Fetch transporter data on component mount
  useEffect(() => {
    if (id) {
      fetchTransporter();
    }
  }, [id]);

  // Fetch transporter details
  const fetchTransporter = async () => {
    try {
      setIsLoading(true);
      const response = await TransporterAPI.getTransporter(id!);

      if (response.success) {
        setTransporter(response.data);
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch transporter details",
          variant: "destructive"
        });
        navigate('/transporters');
      }
    } catch (error) {
      console.error("Error fetching transporter:", error);
      toast({
        title: "Error",
        description: "Failed to fetch transporter details",
        variant: "destructive"
      });
      navigate('/transporters');
    } finally {
      setIsLoading(false);
    }
  };

  // Format date helper
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';

    try {
      return format(new Date(dateString), 'PPP');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get verification status badge
  const getVerificationBadge = () => {
    if (!transporter) return null;
    return <StatusBadge status={transporter.verificationStatus} />;
  };

  // Handle deleting a vehicle
  const handleDeleteVehicle = async (vehicleId: string) => {
    if (!window.confirm("Are you sure you want to delete this vehicle? This action cannot be undone.")) {
      return;
    }

    try {
      setIsDeletingVehicle(vehicleId);

      const response = await TransporterAPI.deleteVehicle(id!, vehicleId);

      if (response.success) {
        toast({
          title: "Success",
          description: "Vehicle deleted successfully"
        });

        // Refresh transporter data
        fetchTransporter();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to delete vehicle",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      toast({
        title: "Error",
        description: "Failed to delete vehicle",
        variant: "destructive"
      });
    } finally {
      setIsDeletingVehicle(null);
    }
  };

  // Handle deleting a driver
  const handleDeleteDriver = async (driverId: string) => {
    if (!window.confirm("Are you sure you want to delete this driver? This action cannot be undone.")) {
      return;
    }

    try {
      setIsDeletingDriver(driverId);

      const response = await TransporterAPI.deleteDriver(driverId);

      if (response.success) {
        toast({
          title: "Success",
          description: "Driver deleted successfully"
        });

        // Refresh transporter data
        fetchTransporter();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to delete driver",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error deleting driver:", error);
      toast({
        title: "Error",
        description: "Failed to delete driver",
        variant: "destructive"
      });
    } finally {
      setIsDeletingDriver(null);
    }
  };

  // Handle deleting a document
  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm("Are you sure you want to delete this document? This action cannot be undone.")) {
      return;
    }

    try {
      setIsDeletingDocument(documentId);

      // Assuming there's an API method to delete a document
      const response = await TransporterAPI.deleteDriverDocument(id!, documentId);

      if (response.success) {
        toast({
          title: "Success",
          description: "Document deleted successfully"
        });

        // Refresh transporter data
        fetchTransporter();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to delete document",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive"
      });
    } finally {
      setIsDeletingDocument(null);
    }
  };

  // Render function
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px]">
        <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
        <p className="mt-4 text-gray-500">Loading transporter details...</p>
      </div>
    );
  }

  if (!transporter) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px]">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="mt-4 text-gray-700">Transporter not found</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/transporters')}
        >
          Back to Transporters
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate('/transporters')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{transporter.name}</h1>
          {((transporter as any).companyName || (transporter as any).email || (transporter as any).mobile) && (
            <p className="text-gray-500">
              {(transporter as any).companyName || (transporter as any).email || (transporter as any).mobile}
            </p>
          )}
        </div>
      </div>

      <Tabs
        defaultValue="overview"
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="drivers">Drivers</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <TransporterDashboard transporter={transporter} />
        </TabsContent>

        <TabsContent value="vehicles" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Vehicles</h2>
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Registration</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Capacity</TableHead>
                    <TableHead>Insurance Expiry</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!transporter.vehicles || transporter.vehicles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <p className="text-gray-500">No vehicles found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transporter.vehicles.map(vehicle => (
                      <TableRow key={vehicle._id}>
                        <TableCell>{vehicle.registrationNumber}</TableCell>
                        <TableCell className="capitalize">{vehicle.type}</TableCell>
                        <TableCell>
                          {vehicle.capacity.value} {vehicle.capacity.unit}
                        </TableCell>
                        <TableCell>{formatDate(vehicle.insuranceExpiryDate)}</TableCell>
                        <TableCell>
                          <StatusBadge status={vehicle.isActive ? 'active' : 'inactive'} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500"
                            disabled={isDeletingVehicle === vehicle._id}
                            onClick={() => handleDeleteVehicle(vehicle._id)}
                          >
                            {isDeletingVehicle === vehicle._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="drivers" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Drivers</h2>
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>License</TableHead>
                    <TableHead>License Expiry</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!transporter.drivers || transporter.drivers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <p className="text-gray-500">No drivers found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transporter.drivers.map(driver => (
                      <TableRow key={driver._id}>
                        <TableCell>{driver.name}</TableCell>
                        <TableCell>{driver.mobile}</TableCell>
                        <TableCell>{driver.licenseNumber}</TableCell>
                        <TableCell>{formatDate(driver.licenseExpiry)}</TableCell>
                        <TableCell>
                          <StatusBadge status={driver.isActive ? 'active' : 'inactive'} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500"
                            disabled={isDeletingDriver === driver._id}
                            onClick={() => handleDeleteDriver(driver._id)}
                          >
                            {isDeletingDriver === driver._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Documents</h2>
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Uploaded At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!transporter.documents || transporter.documents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8">
                        <p className="text-gray-500">No documents found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transporter.documents.map(document => (
                      <TableRow key={document._id}>
                        <TableCell>{document.name}</TableCell>
                        <TableCell className="capitalize">{document.type.replace('_', ' ')}</TableCell>
                        <TableCell>{formatDate(document.uploadedAt)}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(document.fileUrl, '_blank')}
                          >
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500"
                            disabled={isDeletingDocument === document._id}
                            onClick={() => handleDeleteDocument(document._id)}
                          >
                            {isDeletingDocument === document._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 